import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { apiGet, API_URL, getAdminToken } from '../utils/api';
import ReportFilters from './ReportFilters';

const PatientStatistics = () => {
  const { print_lang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({});
  const printRef = useRef();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.branch_id) params.append('branch_id', filters.branch_id);
      if (filters.preset) params.append('preset', filters.preset);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);

      const response = await apiGet(`/api/admin/reports/patients?${params.toString()}`);
      if (response.success) {
        setData(response.data);
      }
    } catch (err) {
      console.error(err.message || 'Failed to load patient statistics', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    const params = new URLSearchParams();
    if (filters.branch_id) params.append('branch_id', filters.branch_id);

    try {
      const token = getAdminToken();
      const response = await fetch(`${API_URL}/api/admin/reports/export/patients?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'patient_report.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export report');
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${print_lang('reports.patients', 'Patient Statistics')}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            h1 { margin-bottom: 20px; }
            h3 { margin-top: 30px; margin-bottom: 10px; }
            .grid { display: flex; gap: 20px; flex-wrap: wrap; }
            .grid > div { flex: 1; min-width: 250px; }
            @media print { body { -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getGenderLabel = (gender) => {
    const labels = {
      male: print_lang('patient.male', 'Male'),
      female: print_lang('patient.female', 'Female'),
      other: print_lang('patient.other', 'Other'),
      Unknown: print_lang('common.unknown', 'Unknown')
    };
    return labels[gender] || gender;
  };

  const getAgeGroupLabel = (ageGroup) => {
    const labels = {
      'Under 18': print_lang('reports.under18', 'Under 18'),
      '18-30': '18-30',
      '31-45': '31-45',
      '46-60': '46-60',
      'Over 60': print_lang('reports.over60', 'Over 60'),
      'Unknown': print_lang('common.unknown', 'Unknown')
    };
    return labels[ageGroup] || ageGroup;
  };

  return (
    <div>
      <ReportFilters
        onFilterChange={setFilters}
        showBranchFilter={true}
        showDateFilter={true}
        onExport={handleExport}
        onPrint={handlePrint}
        loading={loading}
      />

      {loading ? (
        <div className="card">
          <div className="loading-container">
            <div className="spinner" />
          </div>
        </div>
      ) : data ? (
        <div ref={printRef}>
          <h2 style={{ marginBottom: '1rem' }}>{print_lang('reports.patients', 'Patient Statistics')}</h2>

          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="card" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{print_lang('reports.totalPatients', 'Total Patients')}</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{data.summary?.total_patients || 0}</div>
            </div>
            <div className="card" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{print_lang('reports.activePatients', 'Active Patients')}</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{data.summary?.active_patients || 0}</div>
            </div>
            {/* Ramp stops are written out rather than var(--brand-*): this subtree is
                cloned into a bare print window with no theme.css, where the custom
                properties resolve to nothing and leave white text on a blank card. */}
            <div className="card" style={{ background: 'linear-gradient(135deg, #2F6B58 0%, #20463B 100%)', color: 'white' }}>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{print_lang('reports.newPatients', 'New Patients')}</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{data.summary?.new_patients || 0}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>{print_lang('reports.inPeriod', '(In Period)')}</div>
            </div>
          </div>

          {/* Charts Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            {/* By Gender */}
            {data.by_gender && data.by_gender.length > 0 && (
              <div className="card">
                <h3 style={{ marginBottom: '1rem' }}>{print_lang('reports.byGender', 'By Gender')}</h3>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>{print_lang('reports.gender', 'Gender')}</th>
                        <th style={{ textAlign: 'right' }}>{print_lang('reports.count', 'Count')}</th>
                        <th style={{ textAlign: 'right' }}>{print_lang('reports.percentage', '%')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_gender.map((row, index) => {
                        const percentage = data.summary?.total_patients > 0
                          ? ((row.count / data.summary.total_patients) * 100).toFixed(1)
                          : 0;
                        return (
                          <tr key={index}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  backgroundColor: row.gender === 'male' ? '#3b82f6' :
                                                   row.gender === 'female' ? '#ec4899' : '#9ca3af'
                                }} />
                                {getGenderLabel(row.gender)}
                              </div>
                            </td>
                            <td style={{ textAlign: 'right' }}>{row.count}</td>
                            <td style={{ textAlign: 'right' }}>{percentage}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* By Age Group */}
            {data.by_age_group && data.by_age_group.length > 0 && (
              <div className="card">
                <h3 style={{ marginBottom: '1rem' }}>{print_lang('reports.byAgeGroup', 'By Age Group')}</h3>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>{print_lang('reports.ageGroup', 'Age Group')}</th>
                        <th style={{ textAlign: 'right' }}>{print_lang('reports.count', 'Count')}</th>
                        <th style={{ textAlign: 'right' }}>{print_lang('reports.percentage', '%')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_age_group.map((row, index) => {
                        const total = data.by_age_group.reduce((sum, r) => sum + r.count, 0);
                        const percentage = total > 0 ? ((row.count / total) * 100).toFixed(1) : 0;
                        return (
                          <tr key={index}>
                            <td>{getAgeGroupLabel(row.age_group)}</td>
                            <td style={{ textAlign: 'right' }}>{row.count}</td>
                            <td style={{ textAlign: 'right' }}>{percentage}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* By Branch */}
          {data.by_branch && data.by_branch.length > 0 && (
            <div className="card">
              <h3 style={{ marginBottom: '1rem' }}>{print_lang('reports.byBranch', 'By Branch')}</h3>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>{print_lang('reports.branch', 'Branch')}</th>
                      <th style={{ textAlign: 'right' }}>{print_lang('reports.patientCount', 'Patients')}</th>
                      <th style={{ textAlign: 'right' }}>{print_lang('reports.percentage', '%')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_branch.map((row, index) => {
                      const percentage = data.summary?.total_patients > 0
                        ? ((row.patient_count / data.summary.total_patients) * 100).toFixed(1)
                        : 0;
                      return (
                        <tr key={index}>
                          <td>{row.branch_name || `Branch ${row.branch_id}`}</td>
                          <td style={{ textAlign: 'right' }}>{row.patient_count}</td>
                          <td style={{ textAlign: 'right' }}>{percentage}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      <td>{print_lang('reports.total', 'Total')}</td>
                      <td style={{ textAlign: 'right' }}>{data.summary?.total_patients || 0}</td>
                      <td style={{ textAlign: 'right' }}>100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <h3 className="empty-state-title">{print_lang('common.noData', 'No data found')}</h3>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientStatistics;
